import type {
  FamilySignatureMatch,
  Finding,
  FingerprintMatch,
  KnownHashMatch,
  ScanReport,
  Severity,
} from "./types";

/**
 * Risk model: how much evidence becomes a number, and what that number means.
 *
 * This is policy, not detection, so it does not belong in the engine that
 * produces the evidence. Keeping it here means the whole model -- every
 * constant, both curves, the composition and the verdict bands -- can be read
 * and tested as one artefact, and a change to how risk is graded lands in one
 * file instead of somewhere inside a 4,000-line pipeline.
 *
 * Two things deliberately live here rather than in the caller:
 *
 *  - The band thresholds, because the interface used to restate them to pick an
 *    icon, which meant a band could move while the UI kept the old ladder.
 *  - The two axes. Behaviour and dependency exposure are pooled separately and
 *    composed, so the composition cannot be quietly re-implemented downstream.
 */

// These rules describe what an archive *can* do, not what it was observed
// doing: every one of them matches on API surface alone. A large dependency
// trips several of them by simply existing, which is why an unproven archive
// weights them down instead of letting them decide the verdict.
// Intent-bearing findings are deliberately absent: credential access, embedded
// secrets, the download-then-execute chain, capture, persistence, tampering and
// the stealer correlations all score in full no matter what, because they say
// something about purpose rather than about API surface.
//
// This list is the single owner of that judgement. The analyzer asks the same
// question when it wants to note that an archive only ever showed API surface,
// and it asks through isCapabilitySurfaceRule rather than holding a second copy
// of the ids -- two lists would drift, and the note would then describe a
// different set of rules than the one that got discounted.
const CAPABILITY_RULE_IDS = new Set([
  "JARLAB-NET-001",
  "JARLAB-LOAD-001",
  "JARLAB-NATIVE-001",
  "JARLAB-EVASION-001",
  "JARLAB-EXEC-001",
  "JARLAB-COMPOUND-002",
  "JARLAB-ACTION-PROCESS-EXECUTION",
  "JARLAB-ACTION-REMOTE-DOWNLOAD-TO-DISK",
  "JARLAB-ACTION-ARCHIVE-EXTRACTION",
]);

// API surface is only evidence if the archive can actually reach it. A
// dependency with no entry point and no proven data flow mentions
// URLClassLoader and HttpURLConnection because it is a library, not because it
// is going to fetch and execute anything, so those findings are heavily
// discounted -- the findings still appear, but they cannot decide the verdict.
//
// The value is tied to BEHAVIOUR_MIDPOINT, not chosen independently: it has to
// keep a realistic unproven library under the Suspicious threshold, and the
// previous midpoint of 40 left room for a gentler 0.35 that the new midpoint
// would have promoted to "Suspicious" for ordinary library code.
const UNPROVEN_CAPABILITY_WEIGHT = 0.15;

/**
 * Whether a finding only describes what the archive could do, rather than what
 * it was caught doing.
 *
 * Exported so the engine can raise its "unproven capability" note from the same
 * list that decides the discount. A predicate rather than the set itself, so
 * the list stays owned here and cannot be edited from outside.
 */
export function isCapabilitySurfaceRule(ruleId: string): boolean {
  return CAPABILITY_RULE_IDS.has(ruleId);
}

// Evidence mass is the primary term. A single proven critical is already a
// serious signal and a hundred criticals are not a hundred times more serious
// than one, so the total is passed through a Hill curve rather than summed
// linearly and clipped at the ceiling. The curve is flat at the origin and
// saturates at 100, which is the shape a risk score needs: thin evidence
// barely moves it, overwhelming evidence approaches the top without ever
// colliding with it, and no single steep region distorts the middle of the
// scale.
//
// The scale must stay ordered all the way up. A score that is `min(100, ...)`
// of anything is not ordered above that point: every sample past the clip
// reports the same number, and an analyst can no longer tell a bad sample from
// a catastrophic one. That is why nothing here is added after the curve and
// clipped -- the composition below is bounded by construction, so no input can
// reach 100 and every step of evidence still moves the number.
const SEVERITY_MASS: Record<Severity, number> = {
  critical: 30,
  high: 14,
  medium: 7,
  low: 2.5,
  info: 0,
};

// Confidence discounts continuously instead of stepping, so a finding that is
// corroborated but not proven keeps meaningful weight rather than falling off
// a cliff the moment it drops from "medium" to "low".
const CONFIDENCE_FACTOR: Record<Finding["confidence"], number> = {
  high: 1,
  medium: 0.8,
  low: 0.55,
};

// Mass at which the behaviour curve crosses 50, and how hard it climbs.
//
// The midpoint is the load-bearing number and it is set from the verdict bands
// rather than from a fixture. What the bands claim to mean:
//
//   >= 85  Malicious   several independent things this archive does
//   >= 60  High risk   clear evidence of a serious capability
//   >= 30  Suspicious  worth an analyst's time, not yet proven
//
// A proven critical finding -- an archive carrying a live exfiltration secret,
// say -- is unambiguous evidence of a serious capability, so it belongs in High
// risk by itself: 30 mass, which is 1.5x the midpoint and lands near 65. One
// high finding is 14 mass and lands near 37, Suspicious, which is the honest
// reading of a single pattern match. Two independent highs reach 28 and cross
// into High risk. Three reach 42; six reach 84 and the evidence is
// overwhelming.
//
// A midpoint of 40 (the previous value) put one critical at 39 and one high at
// 17, which read a confirmed credential-stealing pattern match as "Low risk".
// That is the curve over-compressing: the severity table already says how
// serious a finding is, and asking the curve to say it a second time halves it.
const BEHAVIOUR_MIDPOINT = 20;
const MASS_STEEPNESS = 1.5;

// A known-vulnerable dependency is evidence of exposure, not evidence of
// malice, and the model now says so structurally rather than only in prose.
//
// The reasoning: an advisory describes a *component*, not the *artifact*. A jar
// bundling log4j-core 2.14.1 and a jar that is Log4Shell are indistinguishable
// from the coordinate alone; only what the code does separates them. Treating
// advisories as behavioural mass therefore reported a library that merely ships
// an old library as "Malicious" -- a jar whose only finding was 70 advisories
// and not one malicious behaviour scored 99.
//
// The old model was already half-aware of this: it excluded `dependency` from
// CORROBORATING_CATEGORIES, correctly refusing to treat a tidy dependency list
// as independent support, while still feeding those findings full severity
// mass. This is the same judgement applied consistently.
//
// So advisories get their own axis, discounted because an advisory is weaker
// evidence of malice than a finding about the code itself, and capped below
// the Malicious threshold so that no quantity of known-vulnerable components
// can call an artifact malware. The detail an analyst needs -- which advisory,
// which CVSS, which fixed version -- is carried by the findings themselves.
const DEPENDENCY_MASS_FACTOR = 0.2;
const DEPENDENCY_MIDPOINT = 25;

// The ceiling is a design commitment, not a tuning value: it is below 85, so
// dependency exposure alone can never reach the Malicious band no matter how
// many advisories match. A jar shipping seventy known-vulnerable components is
// a serious operational problem and is reported as one, but calling it
// malware is a category error the analyst will learn to distrust.
const DEPENDENCY_CEILING = 55;

const REPEAT_CREDIT = 0.15;
const MAX_REPEAT_CREDIT = 4;

// Corroboration is what separates a real sample from a pile of coincidental
// hits. Independent categories agreeing with each other is the strongest
// conclusion static analysis can reach, because no single rule can produce it.
// Shape-only categories are excluded: a tidy manifest is not independent
// support for a credential-stealing finding.
const CORROBORATING_CATEGORIES = new Set<Finding["category"]>([
  "credentials",
  "execution",
  "network",
  "evasion",
  "persistence",
  "collection",
  "signature",
]);

// A category counts as corroboration only once it holds evidence heavy enough
// to stand behind. This also excludes unproven capabilities on their own,
// since those are discounted below the bar when the archive cannot start
// itself.
//
// The ceiling is deliberately a quarter of the scale. The mass curve compresses
// hard in the upper half, so without a meaningful term for qualitative
// agreement a sample carrying a critical exfiltration credential, credential
// theft and command execution could not reach the top band however clear the
// evidence was. Corroboration is the one signal that distinguishes a real
// sample from a pile of coincidental hits, and an archive that trips three
// independent parts of the kill chain has earned it.
const CORROBORATION_MASS = 4;
const CORROBORATION_STEP = 6;
const MAX_CORROBORATION = 24;

// Identity evidence sets a floor rather than adding mass, because it answers a
// different question. Mass asks "how bad does this look?"; identity asks "what
// is this?" An exact match against a trusted record means the bytes in hand
// are the bytes that were catalogued, so the sample is identified rather than
// suspected, and no amount of quiet code should pull that verdict down.
//
// Exported with calculateScore so the model stays auditable: a number on a
// report should be reproducible from its stated inputs without reading the
// whole pipeline.
export function identityFloor(
  knownHashMatches: KnownHashMatch[],
  familySignatureMatches: FamilySignatureMatch[],
  fingerprintMatches: FingerprintMatch[] = [],
) {
  const trustedHash = knownHashMatches.some(
    (match) => match.trust !== "local-unverified",
  );
  if (trustedHash) return 97;

  // A structural near-duplicate in a signed pack is identity evidence of the
  // same standing as an exact hash. The bytes differ, but the pack author set
  // the threshold and signed their name to it, so re-grading the similarity
  // here would second-guess the person who published the corpus. Unverified
  // packs contribute findings for review and no floor, exactly as an
  // unverified hash record does.
  const trustedFingerprint = fingerprintMatches.some(
    (match) => match.trust !== "local-unverified",
  );
  if (trustedFingerprint) return 97;

  // A family profile that cleared its own curated minimum is strong, but it is
  // still a pattern match, so it raises a floor without claiming identity. How
  // far it raises depends on how much of the profile actually matched.
  const strongest = familySignatureMatches
    .filter((match) => match.confidence === "high")
    .reduce(
      (top, match) => Math.max(top, match.score / match.maxScore),
      0,
    );
  if (strongest <= 0) return 0;
  return Math.round(70 + 20 * Math.min(1, strongest));
}

// Maps raw evidence mass onto 0-100, staying monotonic and finite at both
// ends so no finding can produce Infinity or NaN. The result is always strictly
// below 100 for any finite input, which is what lets the two axes be composed
// without ever needing a clip.
function hill(mass: number, midpoint: number) {
  const x = (Math.max(0, mass) / midpoint) ** MASS_STEEPNESS;
  return (100 * x) / (1 + x);
}

// The top of the heuristic scale is deliberately 99, not 100.
//
// 100 is reserved for certainty, and static analysis can never be certain: it
// never runs the sample, and "no rule fired" is not "this is safe". The only
// evidence that justifies certainty is identity -- a match against a signed
// record saying these bytes are the catalogued bytes -- and that arrives
// through identityFloor, not through here.
//
// Composing onto [0, 99] rather than [0, 100] makes that structural instead of
// aspirational. The alternative, `Math.min(100, ...)`, is precisely the defect
// this replaces: a bare clip that leaves every sample past it reporting the
// same number. Rescaling costs one point across the whole scale and makes 100
// unreachable for any input at all, while leaving the ordering untouched.
const HEURISTIC_CEILING = 99;

// Combines two independent 0-100 axes into one number that is strictly
// increasing in each, never exceeds either by more than the other, and can
// never reach 100. This is a noisy-OR: it behaves like addition when both
// axes are small, like "the greater of the two" when one dominates, and
// saturates smoothly in between.
//
// The alternative -- `Math.min(100, behaviour + dependency)` -- is what produced
// the flat maximum, because once the sum crossed 100 every further piece of
// evidence stopped moving the number. Here the only way to approach the ceiling
// is to have both axes high, so an analyst reading 95 knows the risk is broad
// rather than an artifact of one crowded axis.
function compose(behaviour: number, dependency: number) {
  const combined = 1 - (1 - behaviour / 100) * (1 - dependency / 100);
  return HEURISTIC_CEILING * combined;
}

export function calculateScore(
  findings: Finding[],
  capabilityProven = true,
  floor = 0,
) {
  const grouped = new Map<
    string,
    { strongest: number; repeats: number; dependency: boolean }
  >();
  const categoryMass = new Map<Finding["category"], number>();

  for (const finding of findings) {
    // Advisories are the only findings in the `dependency` category, and they
    // answer a different question from everything else, so they are pooled
    // separately rather than discounted in place. See DEPENDENCY_MASS_FACTOR.
    const dependency = finding.category === "dependency";
    const mass =
      SEVERITY_MASS[finding.severity] *
      CONFIDENCE_FACTOR[finding.confidence] *
      (dependency
        ? DEPENDENCY_MASS_FACTOR
        : capabilityProven || !CAPABILITY_RULE_IDS.has(finding.ruleId)
          ? 1
          : UNPROVEN_CAPABILITY_WEIGHT);
    if (mass <= 0) continue;

    const group = grouped.get(finding.ruleId);
    if (group) {
      group.strongest = Math.max(group.strongest, mass);
      group.repeats += 1;
    } else {
      grouped.set(finding.ruleId, { strongest: mass, repeats: 1, dependency });
    }

    // Corroboration only ever counts behavioural categories, so dependency
    // advisories can never contribute here. That was already true, and it is
    // the same judgement that now keeps them off the behaviour axis.
    if (!dependency) {
      categoryMass.set(
        finding.category,
        Math.max(categoryMass.get(finding.category) ?? 0, mass),
      );
    }
  }

  let behaviourMass = 0;
  let dependencyMass = 0;
  for (const { strongest, repeats, dependency } of grouped.values()) {
    // A rule firing on forty classes is better evidence than one firing once,
    // but not forty times better, so repeats add a capped sliver.
    const withRepeats =
      strongest +
      strongest *
        REPEAT_CREDIT *
        Math.min(MAX_REPEAT_CREDIT, Math.max(0, repeats - 1));
    if (dependency) dependencyMass += withRepeats;
    else behaviourMass += withRepeats;
  }

  let categories = 0;
  for (const [category, mass] of categoryMass) {
    if (CORROBORATING_CATEGORIES.has(category) && mass >= CORROBORATION_MASS) {
      categories += 1;
    }
  }
  // Folded into the mass before the curve rather than added to the score after
  // it. On the score it was a flat bonus that could push a sample into the
  // clip; inside the curve it is still the qualitative lift it was meant to be,
  // and it stays ordered.
  behaviourMass += Math.min(
    MAX_CORROBORATION,
    Math.max(0, categories - 1) * CORROBORATION_STEP,
  );

  const behaviourAxis = hill(behaviourMass, BEHAVIOUR_MIDPOINT);
  const dependencyAxis = DEPENDENCY_CEILING * (hill(dependencyMass, DEPENDENCY_MIDPOINT) / 100);
  return Math.max(floor, Math.round(compose(behaviourAxis, dependencyAxis)));
}

export function verdictFor(score: number): ScanReport["verdict"] {
  if (score >= 85) return "Malicious";
  if (score >= 60) return "High risk";
  if (score >= 30) return "Suspicious";
  if (score > 0) return "Low risk";
  return "No obvious indicators";
}
